// src/oura/components/docs/FolderCard.tsx
//
// The client's Google Drive folder: pasted once, then read by Oura's own
// Google account every 5 minutes. That account is the SERVER's (route 1's
// `actingAs`), never a literal: it is oura@ourteam.ai on production and
// no-reply@ourteam.ai on staging, and a staffer told the wrong one shares the
// folder with an account that cannot read it (#3974). Linked, it says what Oura read and when,
// with "Read now", "Change folder" and an unlink, each change behind a
// confirm. Not linked, it takes the folder's link, checks its shape before
// sending it, and says in words why the server refused one.
//
// Linking and unlinking swap what the card shows once the board is read
// again, so focus follows to the new state instead of dropping to the page.
//
// It reads the folder off the documents board's own load, so the two cards
// never disagree about the folder.

import { useCallback, useEffect, useId, useRef, useState } from "react";
import { ExternalLinkIcon, FolderIcon, FolderPlusIcon, Link2Icon, Loader2Icon, RefreshCwIcon } from "lucide-react";
import {
    createDocsFolder,
    linkDocsFolder,
    parseDriveFolderLink,
    readDocsFolderNow,
    unlinkDocsFolder,
    type BusinessDocuments,
    type DocsConnection,
    type DocsFailure,
    type DocsFolder,
} from "@service/aeo/clientDocs";
import { useSnackbar } from "@hook/use-snackbar";
import { useWritesPaused } from "@context/maintenance";
import { cn } from "../../lib/cn";
import { ouraHref } from "../../nav";
import { ArrowLink, FOLDER_STATUS, FoldCard, LATE_TEXT, PillButton, TYPE, Tag, folderErrorStatus, type StatusStyle } from "@oura/ui";
import { agoText, docsFailureText, folderAccessHint, folderReadsAsText, isReadStale, plural, useDocsConnection, type DocsLoad } from "@oura/docs";
import { TAP_TARGET, useNow } from "./boardModel";

/** The link field's DOM id, so the board's "Link the folder" can put the cursor in it. */
export const FOLDER_LINK_FIELD_ID = "oura-docs-folder-link";

const FIELD = cn(
    TYPE.tb,
    "block h-10 w-full rounded-xl border-0 px-3 text-gray-900 placeholder:text-gray-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-600",
);

/** How long "Read now" waits for the read to land before it stops asking. */
const READ_NOW_WAIT_MS = 120_000;
const READ_NOW_EVERY_MS = 5_000;

/** The folder's state as a tag, in the words Connectors and All clients use: connected, no access, not read yet, not linked. */
function folderTag(folder: DocsFolder | null): StatusStyle {
    if (!folder) return FOLDER_STATUS.not_linked;
    if (folder.lastError) return FOLDER_STATUS[folderErrorStatus(folder.lastError.code)];
    if (!folder.lastReadAt) return FOLDER_STATUS.not_read;
    return FOLDER_STATUS.connected;
}

/** What the server said when it refused a link, in words, with where the folder already belongs. */
function LinkRefusal({ failure }: { failure: DocsFailure }) {
    const text = failure.reason === "network" ? "The folder did not link. Try again." : docsFailureText(failure, "the folder");
    const other = failure.code === "folder_linked_elsewhere" ? failure.linkedTo : null;
    // ambiguous_folder: each folder with the name, to open and paste the link of the one to use.
    const folders = failure.code === "ambiguous_folder" ? failure.folders : null;
    return (
        <>
            {text}
            {other && (
                <>
                    {" "}
                    <ArrowLink to={ouraHref("reviews", { role: "oura-staff", businessId: other.businessId })}>Open {other.name ?? "that client"}</ArrowLink>
                </>
            )}
            {folders?.map((f, i) => (
                <span key={f.id}>
                    {" "}
                    <a
                        href={f.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-blue-600 underline hover:text-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
                    >
                        Open folder {i + 1}
                        <span className="sr-only"> (opens Google Drive)</span>
                    </a>
                </span>
            ))}
        </>
    );
}

function LinkForm({
    businessId,
    connection,
    current = null,
    focusOnMount = false,
    onFocused,
    onDone,
    onCancel,
    canMake = false,
    onMakeUnavailable,
    onStale,
}: {
    businessId: string;
    /** Route 1, for the account to share the folder with. Null while it is still being read. */
    connection: DocsConnection | null;
    /** The folder linked now, when this form changes it: no intro, and "Keep it" to back out. */
    current?: DocsFolder | null;
    /** Put the cursor in the field once the form shows. */
    focusOnMount?: boolean;
    onFocused?: () => void;
    /** After the server took the link. */
    onDone?: () => void;
    onCancel?: () => void;
    /** Offer "Create folder in Google Drive" (unlinked only: a link is never replaced by it). */
    canMake?: boolean;
    /** The route is not deployed here (404): hide the create button for the session. */
    onMakeUnavailable?: () => void;
    /** The client already has a folder (already_linked): read the board again, no error. */
    onStale?: () => void;
}) {
    const snackbar = useSnackbar();
    const errorId = useId();
    const helpId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const [url, setUrl] = useState("");
    // One request at a time, for paste and create alike. The ref stops a second
    // click before React has re-rendered the busy state.
    const [working, setWorking] = useState<"link" | "make" | null>(null);
    const inFlight = useRef(false);
    const writesPaused = useWritesPaused();
    const [problem, setProblem] = useState<{ text: string } | { failure: DocsFailure } | null>(null);

    // The field is disabled while a request runs, and React applies that after this
    // handler returns, so focus moves on the next frame, once the field is enabled again.
    const focusField = () => requestAnimationFrame(() => inputRef.current?.focus());

    useEffect(() => {
        if (!focusOnMount) return;
        inputRef.current?.focus();
        onFocused?.();
    }, [focusOnMount, onFocused]);

    const submit = async () => {
        if (inFlight.current || writesPaused) return;
        const check = parseDriveFolderLink(url);
        if (!check.ok) {
            setProblem({ text: check.message });
            inputRef.current?.focus();
            return;
        }
        if (current && check.folderId === current.folderId) {
            setProblem({ text: "That folder is already linked." });
            inputRef.current?.focus();
            return;
        }
        inFlight.current = true;
        setWorking("link");
        setProblem(null);
        // A change is one PUT: the new folder replaces the old, and a refusal leaves the old link in place.
        const r = await linkDocsFolder(businessId, url.trim());
        inFlight.current = false;
        setWorking(null);
        if (!r.ok) {
            setProblem({ failure: r });
            focusField();
            return;
        }
        setUrl("");
        snackbar.showSuccess(current ? "Folder changed." : "Folder linked.", "Oura reads it every 5 minutes.");
        onDone?.();
    };

    // Make the folder in Drive and link it, or link the one already there. Oura takes
    // a while to read it, so the busy line says so.
    const make = async () => {
        if (inFlight.current) return;
        inFlight.current = true;
        setWorking("make");
        setProblem(null);
        const r = await createDocsFolder(businessId);
        inFlight.current = false;
        setWorking(null);
        if (!r.ok) {
            // The create button is hidden or was focused: put focus on the field, not the page.
            focusField();
            if (r.reason === "notServed") {
                onMakeUnavailable?.();
                return;
            }
            if (r.code === "already_linked") {
                onStale?.();
                return;
            }
            setProblem(r.reason === "network" ? { text: "The folder was not made. Try again." } : { failure: r });
            return;
        }
        const name = r.data.name ?? "the folder";
        if (r.data.created) snackbar.showSuccess(`Made the folder ${name} and linked it.`, "Oura reads it every 5 minutes.");
        else snackbar.showSuccess(`Linked the existing folder ${name}.`, "It was already in Drive, so nothing was made.");
        setUrl("");
        onDone?.();
    };

    return (
        <form
            noValidate
            onSubmit={(e) => {
                e.preventDefault();
                void submit();
            }}
        >
            {!current && (
                <p className={cn(TYPE.tb, "m-0 mb-3 text-gray-900")}>
                    Not linked yet. Paste the folder&rsquo;s link once; Oura files every doc in it by the codes in its name.
                </p>
            )}
            <label htmlFor={FOLDER_LINK_FIELD_ID} className={cn(TYPE.tb, "m-0 block font-medium text-gray-900")}>
                {current ? "New Google Drive folder link" : "Google Drive folder link"}
            </label>
            <p id={helpId} className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>
                {folderAccessHint(connection)}
            </p>
            <input
                ref={inputRef}
                id={FOLDER_LINK_FIELD_ID}
                type="url"
                inputMode="url"
                autoComplete="off"
                spellCheck={false}
                disabled={working !== null}
                value={url}
                placeholder="https://drive.google.com/drive/folders/…"
                aria-invalid={problem ? true : undefined}
                aria-describedby={problem ? `${errorId} ${helpId}` : helpId}
                onChange={(e) => {
                    setUrl(e.target.value);
                    if (problem) setProblem(null);
                }}
                // Inside the grey confirm the field needs its own edge.
                className={cn(FIELD, "mt-2", current ? "bg-white ring-1 ring-inset ring-gray-200" : "bg-gray-50")}
            />
            {problem && (
                <p id={errorId} role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-2")}>
                    {"text" in problem ? problem.text : <LinkRefusal failure={problem.failure} />}
                </p>
            )}
            {working === "make" && (
                <p role="status" className={cn(TYPE.tc, "m-0 mt-3 flex items-center gap-2 text-gray-500")}>
                    <Loader2Icon aria-hidden size={12} className="animate-spin motion-reduce:animate-none" />
                    Making the folder in Google Drive. This can take half a minute.
                </p>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
                <PillButton
                    type="submit"
                    variant="primary"
                    icon={Link2Icon}
                    writes
                    busy={working === "link"}
                    disabled={working === "make"}
                    size={current ? "sm" : undefined}
                    className={current ? TAP_TARGET : undefined}
                >
                    {current ? "Change folder" : "Link the folder"}
                </PillButton>
                {!current && canMake && (
                    <PillButton
                        type="button"
                        icon={FolderPlusIcon}
                        busy={working === "make"}
                        writes
                        disabled={working === "link"}
                        onClick={() => void make()}
                    >
                        Create folder in Google Drive
                    </PillButton>
                )}
                {current && onCancel && (
                    <PillButton size="sm" className={TAP_TARGET} onClick={onCancel}>
                        Keep it
                    </PillButton>
                )}
            </div>
        </form>
    );
}

function Linked({
    folder,
    connection,
    reload,
    now,
    focusOnMount = false,
    onFocused,
    onUnlinked,
}: {
    folder: DocsFolder;
    /** Route 1, for the account the reads run as. Null while it is still being read. */
    connection: DocsConnection | null;
    reload: () => void;
    now: number;
    /** Put focus on the folder's name once it shows (just linked). */
    focusOnMount?: boolean;
    onFocused?: () => void;
    onUnlinked?: () => void;
}) {
    const snackbar = useSnackbar();
    const [confirming, setConfirming] = useState(false);
    const [changing, setChanging] = useState(false);
    const [unlinking, setUnlinking] = useState(false);
    const [asking, setAsking] = useState(false);
    const [problem, setProblem] = useState<string | null>(null);
    const nameRef = useRef<HTMLAnchorElement>(null);
    // Focus goes to "Keep it" when the unlink confirm opens, so a second
    // Enter never unlinks, and back to Unlink (or Change folder) when a
    // confirm closes.
    const actionsRef = useRef<HTMLDivElement>(null);
    const focusIn = (selector: string) => requestAnimationFrame(() => actionsRef.current?.querySelector<HTMLButtonElement>(selector)?.focus());

    useEffect(() => {
        if (!focusOnMount) return;
        nameRef.current?.focus();
        onFocused?.();
    }, [focusOnMount, onFocused]);

    // "Read now" is queued: the read lands on a later poll. Ask again every
    // few seconds until the read time (or the failure) moves.
    const mark = `${folder.lastReadAt ?? ""}|${folder.lastError?.at ?? ""}`;
    const [waitingFrom, setWaitingFrom] = useState<{ mark: string; at: number } | null>(null);
    const reading = waitingFrom !== null && waitingFrom.mark === mark && now - waitingFrom.at < READ_NOW_WAIT_MS;
    useEffect(() => {
        if (!reading) return;
        const t = window.setInterval(reload, READ_NOW_EVERY_MS);
        return () => window.clearInterval(t);
    }, [reading, reload]);

    const readNow = async () => {
        setAsking(true);
        setProblem(null);
        const r = await readDocsFolderNow(folder.businessId);
        setAsking(false);
        if (!r.ok) {
            setProblem(r.reason === "network" ? "Oura could not start a read. Try again." : docsFailureText(r, "the folder"));
            return;
        }
        setWaitingFrom({ mark, at: Date.now() });
    };

    const unlink = async () => {
        setUnlinking(true);
        setProblem(null);
        const r = await unlinkDocsFolder(folder.businessId);
        setUnlinking(false);
        if (!r.ok) {
            setProblem(r.reason === "network" ? "The folder did not unlink. Try again." : docsFailureText(r, "the folder"));
            // The busy Unlink was disabled while it had focus.
            focusIn("[data-keep]");
            return;
        }
        setConfirming(false);
        snackbar.showSuccess("Folder unlinked.", "Its documents left the board.");
        onUnlinked?.();
    };

    const stale = isReadStale(folder.lastReadAt, 5, now);
    const ago = agoText(folder.lastReadAt, now);

    return (
        <div>
            <p className={cn(TYPE.tb, "m-0 flex flex-wrap items-center gap-x-2 text-gray-900")}>
                <a
                    ref={nameRef}
                    href={folder.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex min-w-0 items-center gap-1 break-words rounded font-medium text-blue-600 hover:text-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 relative after:absolute after:-inset-x-1 after:-inset-y-[13px] after:content-['']`}
                >
                    {folder.name ?? "The client's folder"}
                    <ExternalLinkIcon aria-hidden size={12} className="shrink-0" />
                    <span className="sr-only">(opens Google Drive)</span>
                </a>

            </p>

            {folder.lastError ? (
                <div className="mt-3 rounded-2xl bg-amber-50 px-4 py-3 text-amber-800 ring-1 ring-inset ring-amber-200">
                    <p className={cn(TYPE.tb, "m-0")}>Oura could not read it. {folder.lastError.message}</p>
                    <p className={cn(TYPE.tc, "m-0 mt-1")}>{ago ? `The last good read was ${ago}.` : "It has not read the folder yet."}</p>
                </div>
            ) : ago ? (
                <p className={cn(TYPE.tb, "m-0 mt-2", stale ? "font-medium text-amber-800" : "text-gray-900")}>
                    {/* The file count belongs to THAT READ, so it is said here
                        rather than beside the folder's name. Up there, with no
                        provenance, it read as a live count of the folder and
                        sat next to a live list of documents that disagreed with
                        it -- "0 files" above one doc just created, "5" above 6
                        (#4051). It is a Drive read, so it only moves when Oura
                        reads the folder again. */}
                    Read by Oura {ago}{folder.fileCount !== null ? `, ${plural(folder.fileCount, "file")} then` : ""}. {stale ? "Reads have stopped." : folderReadsAsText(connection)}
                </p>
            ) : (
                <p className={cn(TYPE.tb, "m-0 mt-2 flex items-center gap-2 text-gray-600")}>
                    <Loader2Icon aria-hidden size={14} className="animate-spin motion-reduce:animate-none" />
                    Oura is reading it for the first time.
                </p>
            )}
            {reading && (
                <p role="status" className={cn(TYPE.tc, "m-0 mt-1 flex items-center gap-2 text-gray-500")}>
                    <Loader2Icon aria-hidden size={12} className="animate-spin motion-reduce:animate-none" />
                    Reading it now.
                </p>
            )}

            {changing ? (
                <div className="mt-3 rounded-xl bg-gray-50 px-3 py-2.5">
                    <p className={cn(TYPE.tb, "m-0 text-gray-900")}>Change this folder?</p>
                    <p className={cn(TYPE.tc, "m-0 mt-0.5 mb-3 text-gray-500")}>
                        Oura reads the new folder instead. This folder&rsquo;s documents leave the board. The files stay in Drive.
                    </p>
                    <LinkForm
                        businessId={folder.businessId}
                        connection={connection}
                        current={folder}
                        focusOnMount
                        onDone={() => {
                            setChanging(false);
                            // The name stays mounted and reads the new folder once the board is read again.
                            requestAnimationFrame(() => nameRef.current?.focus());
                        }}
                        onCancel={() => {
                            setChanging(false);
                            focusIn("[data-change]");
                        }}
                    />
                </div>
            ) : confirming ? (
                <div ref={actionsRef} className="mt-3 rounded-xl bg-gray-50 px-3 py-2.5">
                    <p className={cn(TYPE.tb, "m-0 text-gray-900")}>Unlink this folder?</p>
                    <p className={cn(TYPE.tc, "m-0 mt-0.5 text-gray-500")}>Its documents leave this board. The files stay in Drive.</p>
                    <div className="mt-2 flex flex-wrap gap-2">
                        <PillButton size="sm" variant="primary" busy={unlinking} writes onClick={() => void unlink()} className={TAP_TARGET}>
                            Unlink
                        </PillButton>
                        <PillButton
                            size="sm"
                            className={TAP_TARGET}
                            data-keep=""
                            onClick={() => {
                                setConfirming(false);
                                focusIn("[data-unlink]");
                            }}
                        >
                            Keep it
                        </PillButton>
                    </div>
                </div>
            ) : (
                <div ref={actionsRef} className="mt-3 flex flex-wrap items-center gap-2">
                    <PillButton size="sm" icon={RefreshCwIcon} busy={asking || reading} writes onClick={() => void readNow()} className={TAP_TARGET}>
                        Read now
                    </PillButton>
                    <PillButton
                        size="sm"
                        variant="text"
                        data-change=""
                        writes
                        onClick={() => {
                            setProblem(null);
                            setChanging(true);
                        }}
                    >
                        Change folder
                    </PillButton>
                    <PillButton
                        size="sm"
                        variant="text"
                        data-unlink=""
                        writes
                        onClick={() => {
                            setConfirming(true);
                            focusIn("[data-keep]");
                        }}
                    >
                        Unlink
                    </PillButton>
                </div>
            )}
            {problem && (
                <p role="alert" className={cn(TYPE.tc, LATE_TEXT, "m-0 mt-2")}>
                    {problem}
                </p>
            )}
        </div>
    );
}

export function FolderCard({ businessId, load }: { businessId: string; load: DocsLoad<BusinessDocuments> }) {
    const now = useNow();
    // Read once here, not in each state, so the card runs one poller for the
    // account both of them name.
    const connection = useDocsConnection();
    // Which state takes focus once the board's next read swaps the card: the
    // folder's name after a link, the link field after an unlink. Never on a
    // plain page load.
    const [focusNext, setFocusNext] = useState<"linked" | "form" | null>(null);
    // The create route is not deployed (404): the button is hidden for this page.
    const [makeOff, setMakeOff] = useState(false);
    const focused = useCallback(() => setFocusNext(null), []);
    const ready = load.status === "ready" && load.data !== null;
    const folder = ready ? load.data?.folder ?? null : null;
    const tag = ready ? folderTag(folder) : null;
    const ago = folder ? agoText(folder.lastReadAt, now) : null;

    return (
        // Remounted once the first read is in, so a linked folder starts
        // folded (a secondary tool) and an unlinked one open (a setup
        // action). Linking or unlinking here keeps it as it is, and a choice
        // someone made by hand always wins.
        <FoldCard
            key={ready ? "ready" : "loading"}
            id="folder"
            title="Client folder"
            icon={{ icon: FolderIcon, area: "setup" }}
            defaultFolded={ready && folder !== null}
            right={tag && <Tag tone={tag.tone}>{tag.label}</Tag>}
            foldedFact={ready ? (folder ? (ago ? `Read ${ago}` : "Not read yet") : "Not linked") : null}
        >
            {load.status === "loading" ? (
                <p className={cn(TYPE.tb, "m-0 flex items-center gap-2 text-gray-400")}>
                    <Loader2Icon aria-hidden size={16} className="animate-spin motion-reduce:animate-none" /> Loading
                </p>
            ) : load.status === "error" || !load.data ? (
                <div className="rounded-2xl bg-amber-50 px-4 py-3 text-amber-800 ring-1 ring-inset ring-amber-200">
                    <p className={cn(TYPE.tb, "m-0")}>{load.error ? docsFailureText(load.error, "the folder") : "Could not read the folder."}</p>
                    {load.error?.reason !== "notServed" && (
                        <PillButton size="sm" className="mt-2" onClick={load.reload}>
                            Try again
                        </PillButton>
                    )}
                </div>
            ) : folder ? (
                <Linked
                    folder={folder}
                    connection={connection.data}
                    reload={load.reload}
                    now={now}
                    focusOnMount={focusNext === "linked"}
                    onFocused={focused}
                    onUnlinked={() => setFocusNext("form")}
                />
            ) : (
                <LinkForm
                    businessId={businessId}
                    connection={connection.data}
                    focusOnMount={focusNext === "form"}
                    onFocused={focused}
                    onDone={() => setFocusNext("linked")}
                    canMake={!makeOff}
                    onMakeUnavailable={() => setMakeOff(true)}
                    onStale={load.reload}
                />
            )}
        </FoldCard>
    );
}
